import React from "react";
import { useNavigate } from "react-router-dom";
import aboutbanner from "../assets/aboutbanner.jpg";
import PosterOne from "../share/component.jsx/posterone";
import newimg from '../assets/banner/1.jpeg';



const rhythmCards = [
  {
    title: "Atrial Fibrillation",
    desc: "Rapid, irregular heart rhythm causing palpitations, fatigue, dizziness, or breathlessness. Also increases stroke risk.",
    iconBg: "#FADBD8",
    iconColor: "#C0392B",
    iconPath: require("../assets/symptoms/atrial.png"),
  },
  {
    title: "Tachycardia",
    desc: "Heartbeat that is too fast — felt as racing, pounding, or bursts of rapid beats in the chest or throat.",
    iconBg: "#EAF3DE",
    iconColor: "#3B6D11",
    iconPath: require("../assets/symptoms/tachyCardia.png"),
  },
  {
    title: "Bradycardia",
    desc: "A too-slow heartbeat that can make some individuals feel exhausted, dizzy, or near fainting.",
    iconBg: "#E6F1FB",
    iconColor: "#185FA5",
    iconPath: require("../assets/symptoms/bradycardia.png"),
  },
  {
    title: "Skipped Beats",
    desc: "Early beats of the upper or lower chambers — often dramatic but should be reviewed if occurring frequently.",
    iconBg: "#FAEEDA",
    iconColor: "#854F0B",
    iconPath: require("../assets/symptoms/atrial.png"),
  },
];

const treatments = [
  "Avoiding stimulants such as excessive caffeine, alcohol, or nicotine",
  "Modifying drugs that can increase palpitations",
  "Treating thyroid disease, anemia, sleep apnea, or dehydration",
  "Beta blockers or other rhythm-stabilizing medications when necessary",
  "Cardioversion to restore normal heart rhythm",
  "Catheter ablation for recurrent or difficult arrhythmias",
  "Pacemaker support for certain slow heart rhythms",
  "Stroke prevention strategies as part of atrial fibrillation care",
];

const qaItems = [
  {
    q: "At what time do the palpitations begin?",
    a: "Timing patterns — at night, during stress, or with activity — help narrow the likely cause.",
  },
  {
    q: "How long do they last?",
    a: "Brief episodes versus sustained rhythm problems carry different clinical significance.",
  },
  {
    q: "What medicines or supplements do you take?",
    a: "Many common medications can provoke or worsen palpitations and arrhythmias.",
  },
];

export default function IrregularHeartbeatsPalpitations() {
  const navigate = useNavigate();

  return (
    <div className="ihb-body">
      {/* Hero Banner */}
      <div className="ihb-hero">
        

        <img
        src={require('../assets/symptoms/irregular.png')}
        alt="Chest pain and cardiovascular care"
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
        <div className="ihb-hero-overlay" />
        <div className="ihb-hero-content">
          <span className="ihb-hero-tag">Cardiovascular Health</span>
          <h1>Irregular Heartbeats &amp; Palpitations</h1>
          <p>
            Understanding arrhythmia — from fluttering sensations to atrial
            fibrillation — and when to seek expert care.
          </p>
        </div>
      </div>

      <div className="ihb-page">
        {/* Byline */}
        <div className="ihb-byline">
          <div className="ihb-byline-dot">CV</div>
          <div className="ihb-byline-text">
            <strong>Cardiovascular Physician — Dr. Siam Al Mobarak</strong>
            <span>
              Expert guidance on irregular heartbeat treatments &amp; rhythm
              disorders
            </span>
          </div>
        </div>

        {/* Lead */}
        <p className="ihb-lead">
          An irregular heartbeat may be frightening, abrupt, and very
          disturbing. You might feel a rapid heartbeat, a flutter in the chest,
          a missed beat, or a pounding in the neck — symptoms that may signal an
          arrhythmia requiring careful evaluation.
        </p>

        {/* Section: What Are Palpitations */}
        <div className="ihb-section">
          <h2>What Are Palpitations?</h2>
          <p>
            Pain is not the initial symptom for many patients. It is an odd
            consciousness of the heartbeat. Palpitations can be experienced as
            the heartbeat being too fast, pounding, flipping-flopping,
            fluttering, or skipping. Others experience these feelings in the
            chest, neck, or throat.
          </p>
          <p>
            Although skipped beats are at times harmless premature beats,
            regular or consistent symptoms need to be examined — particularly in
            the event of existing heart disease or other warning signs. Heart
            rhythm disorders occur when the electric impulses that regulate the
            heartbeat do not flow through the heart normally.
          </p>
        </div>

        {/* Section: Rhythm Cards */}
        <div className="ihb-section">
          <h2>Common Rhythm Concerns We Evaluate</h2>
          <div className="ihb-card-grid">
            {rhythmCards.map((card) => (
              <div className="ihb-rhythm-card" key={card.title}>
                <div
                  className="ihb-icon"
                  // style={{ background: card.iconBg }}
                >
                   <img src={card.iconPath} alt="" />
                  
                </div>
                <h3>{card.title}</h3>
                <p>{card.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Callout */}
        <div className="ihb-callout">
          <p>
            <strong>Important:</strong> Palpitations are more urgent when
            accompanied by dizziness, near-fainting, or fainting. These may
            indicate the heart rhythm is impacting blood circulation to the
            brain. Emergency assessment is advised if symptoms worsen with
            shortness of breath, chest pain, or loss of consciousness.
          </p>
        </div>

        {/* Section: Causes */}
        <div className="ihb-section">
          <h2>What Causes Irregular Heartbeats?</h2>
          <p>
            Not all palpitations are indicative of severe heart disease. Common
            non-cardiac triggers include stress, anxiety, dehydration, fever,
            exercise, caffeine, alcohol, nicotine, certain medications, thyroid
            issues, anemia, insomnia, or hormone fluctuations.
          </p>
          <p>
            Nevertheless, arrhythmias may also arise from heart disease, a
            prior heart attack, blocked arteries, valve issues, cardiomyopathy,
            electrolyte imbalance, or age-related changes in the heart's
            electrical system. Symptoms alone cannot always distinguish the
            cause — assessment is essential for new, frequent, or intensifying
            symptoms.
          </p>
        </div>

        {/* Section: Diagnosis */}
        <div className="ihb-section">
          <h2>How Irregular Heartbeats Are Diagnosed</h2>
          <p>
            An attentive diagnosis often begins with your full story of
            symptoms. Your clinician will inquire about timing, duration,
            character (fast, slow, fluttering, or skipped), and any
            accompanying chest pain, shortness of breath, or dizziness.
          </p>

          <div className="ihb-qa-section">
            {qaItems.map((item) => (
              <div className="ihb-qa-item" key={item.q}>
                <div className="ihb-qa-q">{item.q}</div>
                <div className="ihb-qa-a">{item.a}</div>
              </div>
            ))}
          </div>

          <p style={{ marginTop: "1rem" }}>
            Depending on your risk profile, tests may include ECG, ambulatory
            rhythm monitoring (Holter), echocardiogram, thyroid testing,
            electrolyte panels, or heart imaging. Continuous 24-hour or longer
            ECG monitoring is especially useful when symptoms come and go.
          </p>
        </div>

        {/* Section: Treatments */}
        <div className="ihb-section">
          <h2>Irregular Heartbeat Treatments</h2>
          <p>
            Treatment varies according to the cause, arrhythmia type, your
            symptoms, and your risk profile. Reassurance and trigger control may
            be sufficient for some. Others may require medication or a
            procedure.
          </p>
          <ul className="ihb-treatment-list">
            {treatments.map((item) => (
              <li key={item}>
                <span className="ihb-bullet" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Section: Virtual Evaluation */}
        <div className="ihb-section">
          <h2>How Can Virtual Evaluation Help?</h2>
          <p>
            Palpitations are often experienced at home — at nighttime, during
            stress, or between regular office visits. A virtual review can help
            identify symptom patterns, review health history, detect red flags,
            and guide next steps such as ECG testing, Holter monitoring, blood
            work, or urgent care referral.
          </p>
          <p>
            A reliable cardiovascular group can offer viable advice quickly and
            direct you to the appropriate diagnostic pathway — without
            unnecessary delay.
          </p>
        </div>

        {/* Urgent Box */}
        <div className="ihb-urgent-box">
          <h2>When to Seek Urgent Care</h2>
          <p>
            Do not ignore palpitations accompanied by extreme shortness of
            breath, chest pain, fainting, or worsening weakness. Emergency
            treatment is especially necessary when symptoms are new and severe,
            or when you have known heart disease. Even short-lived symptoms
            deserve evaluation — some rhythm issues remain undetected between
            episodes. Early diagnosis helps prevent complications and provides
            more accurate answers sooner.
          </p>
        </div>

        {/* Section: Expert Support */}
        <div className="ihb-section">
          <h2>Expert Support from Cardiovascular Physicians</h2>
          <p>
            The analysis of palpitations must be accurate. It is not just to
            name the symptom — it is to discover the rhythm, comprehend the
            cause, and determine whether treatment is warranted. Skilled
            cardiovascular physicians can identify whether your symptoms
            indicate atrial fibrillation, tachycardia, bradycardia, skipped
            beats, or a different rhythm disorder.
          </p>
          <p>
            Many patients experience relief of symptoms and improved peace of
            mind with appropriate history review, rhythm surveillance, and a
            personalized treatment regimen.
          </p>
        </div>

        {/* Footer CTA */}
        <div className="ihb-footer-cta">
          <p>
            Experiencing fluttering, irregular beats, unexpected acceleration,
            or vertigo from your heartbeat? Do not wait until the issue becomes
            more problematic. Early diagnosis makes treatment simpler, safer,
            and more effective.
          </p>
          <button className="ihb-cta-btn" onClick={() => navigate('/consultations')}>Schedule a Consultation</button>
        </div>
      </div>
    </div>
  );
}





